import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Eyebrow, SectionLabel } from "@/components/Eyebrow";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { motion } from "framer-motion";

const DEFAULTS = {
  gpa: "3.92",
  current_college: "Valencia College",
  intended_major: "Economics",
  target_schools: "Harvard, Yale, UPenn",
  strengths: "Strong macroeconomics coursework, Phi Theta Kappa officer.",
  weaknesses: "No quantitative competitions.",
  activities: "Phi Theta Kappa officer; founded financial-literacy club; tutor.",
};

const inputCls =
  "rounded-none border-stone/50 bg-bone focus-visible:border-ivy focus-visible:ring-0";
const labelCls = "text-[11px] uppercase tracking-[0.22em] text-stone";

export default function IvySignals() {
  const [stage, setStage] = useState("form");
  const [form, setForm] = useState(DEFAULTS);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleFormNext = (e) => {
    e.preventDefault();
    setStage("gate");
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      try {
        await api.post("/leads", {
          name: name || "Diagnostic visitor",
          email,
          current_college: form.current_college,
          source: "ivy_signals_diagnostic",
          message: `Intended major: ${form.intended_major}; targets: ${form.target_schools}`,
          website: "",
        });
      } catch (e) {
        console.warn("Lead capture failed", e);
      }

      const payload = {
        gpa: parseFloat(form.gpa),
        current_college: form.current_college,
        intended_major: form.intended_major,
        target_schools: form.target_schools.split(",").map((s) => s.trim()).filter(Boolean),
        strengths: form.strengths,
        weaknesses: form.weaknesses,
        activities: form.activities,
      };
      const res = await api.post("/diagnostic/ivy-signals", payload);
      setResult(res.data);
      setStage("result");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Diagnostic failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <section className="border-b border-stone/30 bg-bone" data-testid="ivy-signals-page">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-20 lg:pt-28 pb-16 lg:pb-20">
          <Eyebrow>Free tool · Ivy Signals</Eyebrow>
          <h1 className="font-serif font-medium text-4xl sm:text-5xl lg:text-[4.5rem] text-ink leading-[1.04] tracking-tightest mt-5">
            A 60-second read<br />
            <span className="italic text-ivy">on six signals.</span>
          </h1>
          <p className="mt-7 text-ink-soft text-base sm:text-lg max-w-2xl leading-relaxed">
            The six signals admissions officers actually evaluate when reviewing transfer
            applicants. Honest, specific, and not behind a paywall.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          <div className="lg:col-span-3">
            {stage === "form" && (
              <div className="border border-stone/40 bg-bone p-6 sm:p-10">
                <SectionLabel index="A" label="The intake" />
                <form onSubmit={handleFormNext} className="mt-7 space-y-5" data-testid="diagnostic-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gpa" className={labelCls}>GPA</Label>
                      <Input id="gpa" data-testid="input-gpa" className={inputCls} value={form.gpa} onChange={onChange("gpa")} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="current_college" className={labelCls}>Current college</Label>
                      <Input id="current_college" data-testid="input-current-college" className={inputCls} value={form.current_college} onChange={onChange("current_college")} required />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="intended_major" className={labelCls}>Intended major</Label>
                    <Input id="intended_major" data-testid="input-major" className={inputCls} value={form.intended_major} onChange={onChange("intended_major")} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target_schools" className={labelCls}>Target schools (comma-separated)</Label>
                    <Input id="target_schools" data-testid="input-targets" className={inputCls} value={form.target_schools} onChange={onChange("target_schools")} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="strengths" className={labelCls}>Strengths</Label>
                    <Textarea id="strengths" rows={3} data-testid="input-strengths" className={inputCls} value={form.strengths} onChange={onChange("strengths")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weaknesses" className={labelCls}>Weaknesses</Label>
                    <Textarea id="weaknesses" rows={3} data-testid="input-weaknesses" className={inputCls} value={form.weaknesses} onChange={onChange("weaknesses")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="activities" className={labelCls}>Activities</Label>
                    <Textarea id="activities" rows={3} data-testid="input-activities" className={inputCls} value={form.activities} onChange={onChange("activities")} />
                  </div>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-2 bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors"
                    data-testid="diagnostic-next-btn"
                  >
                    Continue <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </form>
              </div>
            )}

            {stage === "gate" && (
              <div className="border border-stone/40 bg-bone p-6 sm:p-10" data-testid="diagnostic-email-gate">
                <Lock className="h-6 w-6 text-brass" />
                <SectionLabel index="B" label="One step before we run it" />
                <h2 className="font-serif font-medium text-2xl sm:text-3xl text-ink mt-4 tracking-tight">
                  Where should we send the read?
                </h2>
                <p className="text-ink-soft text-sm mt-3 max-w-md leading-relaxed">
                  We'll send your results to your inbox and keep you on the (low-volume) Beyond
                  Valencia field-notes list. Unsubscribe anytime.
                </p>
                <form onSubmit={handleEmailSubmit} className="mt-7 space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="diag-name" className={labelCls}>First name</Label>
                    <Input id="diag-name" data-testid="input-diag-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maya" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="diag-email" className={labelCls}>Email</Label>
                    <Input id="diag-email" type="email" data-testid="input-diag-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  <div className="flex flex-wrap gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-8 transition-colors disabled:opacity-60"
                      data-testid="diagnostic-submit-btn"
                    >
                      {loading ? "Analyzing…" : "Run my diagnostic"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStage("form")}
                      className="border border-stone/50 hover:border-ink text-ink-soft uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors"
                    >
                      Back
                    </button>
                  </div>
                </form>
              </div>
            )}

            {stage === "result" && result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-0 border-t border-l border-stone/40"
                data-testid="diagnostic-result"
              >
                <div className="border-r border-b border-stone/40 bg-bone-dim p-7">
                  <Eyebrow>Strongest asset</Eyebrow>
                  <p className="font-serif font-medium text-2xl text-ink mt-3 leading-snug tracking-tight" data-testid="result-strongest-asset">
                    {result.strongest_asset}
                  </p>
                </div>

                <div className="border-r border-b border-stone/40 bg-bone p-7 border-l-2 border-l-brass">
                  <Eyebrow>Biggest narrative risk</Eyebrow>
                  <p className="font-serif font-medium text-xl text-ink mt-3 leading-snug tracking-tight" data-testid="result-risk">
                    {result.biggest_narrative_risk}
                  </p>
                </div>

                <div className="border-r border-b border-stone/40 bg-ivy text-bone p-7">
                  <Eyebrow tone="bone" className="text-brass-soft">Recommended next step</Eyebrow>
                  <p className="font-serif font-medium text-xl mt-3 leading-snug tracking-tight" data-testid="result-next-step">
                    {result.recommended_next_step}
                  </p>
                  <Link
                    to="/services#platform"
                    className="inline-flex items-center gap-2 mt-6 bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-xs font-semibold py-3.5 px-6 transition-colors"
                  >
                    See how Platform ($99/yr) monitors this <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>

                <div className="border-r border-b border-stone/40 bg-bone p-7" data-testid="result-signals">
                  <SectionLabel index="06" label="The six signals" />
                  <div className="space-y-5 mt-6">
                    {result.signals.map((s) => (
                      <div
                        key={s.name}
                        className="border-b border-stone/30 pb-5 last:border-b-0 last:pb-0"
                        data-testid={`signal-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        <div className="flex justify-between items-baseline gap-3">
                          <div className="font-serif text-lg text-ink tracking-tight">{s.name}</div>
                          <div className="font-mono text-sm text-brass tabular-nums">{s.score}/10</div>
                        </div>
                        <div className="mt-3 h-px bg-stone/30 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(s.score / 10) * 100}%` }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                            className="h-px bg-ivy"
                            style={{ height: "2px", marginTop: "-1px" }}
                          />
                        </div>
                        <p className="text-sm text-ink-soft mt-3 leading-relaxed">{s.comment}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-6">
                  <button
                    onClick={() => { setStage("form"); setResult(null); }}
                    className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ivy hover:text-brass transition-colors"
                  >
                    Run again with different inputs →
                  </button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Side rail */}
          <aside className="lg:col-span-2 space-y-0 border-t border-l border-stone/40 self-start">
            <div className="border-r border-b border-stone/40 bg-bone p-6">
              <Eyebrow>How it works</Eyebrow>
              <ol className="mt-4 space-y-3 text-sm text-ink-soft">
                {["Tell us about your record (60s).", "We score 6 admissions signals.", "You get the read instantly — no follow-up calls required."].map((t, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="font-serif text-brass font-medium w-6">{String(i + 1).padStart(2, "0")}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="border-r border-b border-stone/40 bg-bone-dim p-6">
              <Eyebrow>The 6 signals</Eyebrow>
              <ul className="mt-4 space-y-2 text-sm text-ink">
                {["Academic Trajectory","Narrative Clarity","Major Fit","Activity Depth","Letter Strength","Transfer Readiness"].map((s) => (
                  <li key={s} className="flex gap-2.5">
                    <span className="text-brass mt-2 h-px w-3 flex-shrink-0 bg-brass" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}
